import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { MedicalBackgroundQuestionnaireItem } from 'components/Assessments/FuaAssessment/sections/MedicalBackground/questionnaire'
import { GastrointestinalFormField } from './formField'

/**
 * Gastrointestinal subsection adapter
 * Handles the most complex GI/GU assessment with feeding tubes, catheters, nutrition (20 fields)
 * This is the most complex subsection with many interdependent fields
 */
export class GastrointestinalAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // GI assessment
    const giAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT
    )

    const bmDate = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE
    )?.at(0)

    // GU assessment
    const guAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT
    )

    // Catheter assessment
    const catheter = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER
    )?.at(0)

    const catheterType = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE
    )

    // Feeding tube assessment - complex with many related fields
    const feedingTube = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE
    )?.at(0)

    const feedingTubeAmount = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT
    )?.at(0)

    const feedingTotalNumber = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER
    )?.at(0)

    const feedingTubeDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION
    )?.at(0)

    const feedingTubeType = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE
    )?.at(0)

    const feedingTubeSite = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE
    )

    const feedingTubeSize = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE
    )?.at(0)

    const feedingTubeNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES
    )?.at(0)

    // Nutrition assessment
    const nutritionWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL
    )?.at(0)

    const nutritionalFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS
    )

    const dietRequirements = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS
    )?.at(0)

    const nutritionNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES
    )?.at(0)

    const nutritionalScreening = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING
    )

    const historyNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [GastrointestinalFormField.GASTROINTESTINAL_GI_ASSESSMENT]: giAssessment,
      [GastrointestinalFormField.GASTROINTESTINAL_BM_DATE]: bmDate,
      [GastrointestinalFormField.GASTROINTESTINAL_GU_ASSESSMENT]: guAssessment,
      [GastrointestinalFormField.GASTROINTESTINAL_CATHETER]: catheter,
      [GastrointestinalFormField.GASTROINTESTINAL_CATHETER_TYPE]: catheterType,
      [GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE]: feedingTube,
      [GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT]: feedingTubeAmount as number,
      [GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER]:
        feedingTotalNumber as number,
      [GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION]: feedingTubeDuration,
      [GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE]: feedingTubeType,
      [GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE]: feedingTubeSite,
      [GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE]: feedingTubeSize,
      [GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES]: feedingTubeNotes,
      [GastrointestinalFormField.GASTROINTESTINAL_NUTRITION_WNL]: nutritionWnl,
      [GastrointestinalFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS]: nutritionalFactors,
      [GastrointestinalFormField.GASTROINTESTINAL_DIET_REQUIREMENTS]: dietRequirements,
      [GastrointestinalFormField.GASTROINTESTINAL_NUTRITION_NOTES]: nutritionNotes,
      [GastrointestinalFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING]: nutritionalScreening,
      [GastrointestinalFormField.GASTROINTESTINAL_HISTORY_NOTES]: historyNotes,
      [GastrointestinalFormField.GASTROINTESTINAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = [
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_GI_ASSESSMENT)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_BM_DATE)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_GU_ASSESSMENT)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_CATHETER)
      },
      {
        linkId: MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_NUTRITION_WNL)
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING,
        value: this.getFormDataField(
          GastrointestinalFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING
        )
      },
      {
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_SYSTEM_SUMMARY)
      }
    ]

    // Conditionally add catheter type if it exists (conditionally rendered when catheter === 'Yes')
    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_CATHETER_TYPE)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_CATHETER_TYPE)
      })
    }

    // Conditionally add feeding tube related fields if they exist (conditionally rendered when feeding tube === 'Yes')
    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT)
      })
    }

    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER,
        value: this.getFormDataField(
          GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER
        )
      })
    }

    if (
      this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION)
    ) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION,
        value: this.getFormDataField(
          GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION
        )
      })
    }

    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE)
      })
    }

    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE)
      })
    }

    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE)
      })
    }

    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES)
      })
    }

    // Conditionally add nutritional factors if they exist (conditionally rendered when nutrition WNL !== 'Yes')
    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS)
      })
    }

    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_DIET_REQUIREMENTS)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_DIET_REQUIREMENTS)
      })
    }

    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_NUTRITION_NOTES)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_NUTRITION_NOTES)
      })
    }

    if (this.formDataFieldExists(GastrointestinalFormField.GASTROINTESTINAL_HISTORY_NOTES)) {
      mutations.push({
        linkId:
          MedicalBackgroundQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES,
        value: this.getFormDataField(GastrointestinalFormField.GASTROINTESTINAL_HISTORY_NOTES)
      })
    }

    return mutations
  }
}
